import { authFetch } from '@/api/request';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, XCircle, Circle, ChevronDown, ChevronRight } from 'lucide-react';
import { ReactFlow, Handle, Position, useNodesState, useEdgesState, type Node, type Edge } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { usePipelinePoll, type NodeOutput } from '@/hooks/use-pipeline-poll';
import { GddDialogContent, type GddSpec } from '@/components/gdd-card';
import { useWebSocket } from '@/hooks/use-websocket';
import { useAgentStore } from '@/store/agent-store';
import type { StratifiedQaReport } from '@/types';
import { cn } from '@/utils/cn';

interface WorkflowNodeDef { key: string; label: string; nodeKeys: string[] }

const BASE_NODE_DEFS: WorkflowNodeDef[] = [
  { key: 'planning', label: '方案规划', nodeKeys: ['planning'] },
  { key: 'prototype_build', label: '原型构建', nodeKeys: ['prototype_build', 'game_develop', 'game_develop_blueprint', 'prototype_qa'] },
  { key: 'asset_prepare', label: '素材生成', nodeKeys: ['asset_prepare'] },
  { key: 'asset_inject', label: '素材装填', nodeKeys: ['asset_inject'] },
  { key: 'qa_check', label: '质量检测', nodeKeys: ['qa_check'] },
  { key: 'publish', label: '构建发布', nodeKeys: ['publish', 'done'] },
];

export const VIDEO_NODE_DEFS: WorkflowNodeDef[] = [
  { key: 'video_start', label: '视频分析', nodeKeys: ['video-start'] },
  { key: 'video_plan', label: '互动方案', nodeKeys: ['video-confirmation'] },
  // BUG-20260908-PW-002：video-ai-config 归入素材生成（与 pipeline/index.tsx VIDEO_STAGES 保持一致）
  { key: 'asset_prepare', label: '素材生成', nodeKeys: ['video-ai-config', 'video-frame-analyze', 'asset_prepare'] },
  { key: 'video_transcode', label: '视频转码', nodeKeys: ['video-transcode'] },
  { key: 'video_assemble', label: '试玩装配', nodeKeys: ['video-assemble'] },
  { key: 'publish', label: '构建发布', nodeKeys: ['publish', 'done'] },
];

/** 旧链路（视频/批量 BullMQ 链路）节点 key → 新节点 key 归一化映射。
 *  GDD 确认即需求分析，新链路无 requirement 节点；旧链路的 requirement 进度归入方案规划行。 */
const KEY_ALIAS: Record<string, string> = {
  requirement: 'planning',
  image_gen: 'asset_prepare',
  game_gen: 'prototype_build',
  game_develop: 'prototype_build',
  game_develop_blueprint: 'prototype_build', // 历史模板/蓝图键归一到统一原型构建行
  build: 'publish',
  qa: 'qa_check',
};

const IMAGE_CATEGORIES = ['全部', '背景', '角色', '道具', '弹窗', 'UI', '按钮'] as const;

/** 素材槽位名 → 中文分类（与后端 image-prompt slotCategory 语义对齐，弹窗/按钮细分） */
function slotCategoryLabel(slot: string): string {
  if (slot.startsWith('popup_')) return '弹窗';
  if (slot === 'cta_button') return '按钮';
  if (slot === 'guide_hand' || slot.startsWith('option_')) return 'UI';
  if (['background', 'bg_overlay', 'board_bg', 'grid_bg', 'ground', 'road', 'question_bg', 'puzzle_full'].includes(slot)) return '背景';
  if (['character', 'avatar_player', 'avatar_ai', 'character_model'].includes(slot)) return '角色';
  return '道具';
}

function FlowNode({ data }: { data: { label: string; status: string; duration?: number; startedAt?: string; selected?: boolean } }) {
  const [currentTime, setCurrentTime] = useState(Date.now());

  // 动态更新running状态的时间
  useEffect(() => {
    if (data.status !== 'running') return;

    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, [data.status]);

  // 计算运行时长
  const getDuration = () => {
    if (data.status === 'running' && data.startedAt) {
      const start = new Date(data.startedAt).getTime();
      return currentTime - start;
    }
    return data.duration ?? 0;
  };

  const duration = getDuration();

  return (
    <div className={cn('rounded-md border px-2 py-1.5 text-center shadow-sm min-w-[90px] transition-all duration-500',
      data.status === 'running' ? 'border-neon-cyan bg-neon-cyan/10 shadow-neon-cyan/20' : data.status === 'done' ? 'border-neon-green/40 bg-neon-green/5' : data.status === 'failed' ? 'border-red-500/40 bg-red-500/5' : 'border-border bg-card',
      data.selected && 'ring-1 ring-primary/50')}>
      <Handle type="target" position={Position.Top} className="!bg-border !w-1.5 !h-1.5" />
      <div className="flex items-center justify-center gap-1">
        {data.status === 'done' ? <CheckCircle2 size={12} className="text-neon-green" /> : data.status === 'running' ? <Loader2 size={12} className="animate-spin text-neon-cyan" /> : data.status === 'failed' ? <XCircle size={12} className="text-red-400" /> : <Circle size={12} className="text-muted-foreground/40" />}
        <span className={cn('text-[11px] font-medium', data.status === 'pending' ? 'text-muted-foreground' : 'text-foreground')}>{data.label}</span>
      </div>
      {duration > 0 && (
        <p className={cn('mt-0.5 text-[8px] font-mono', data.status === 'running' ? 'text-neon-cyan' : 'text-muted-foreground')}>
          {data.status === 'running' ? '⏱ ' : '✓ '}{(duration / 1000).toFixed(1)}s
        </p>
      )}
      <Handle type="source" position={Position.Bottom} className="!bg-border !w-1.5 !h-1.5" />
    </div>
  );
}
const nodeTypes = { flowNode: FlowNode };

/** P1-1 分层 QA 门禁（L0-L3）层级进度展示：L0/L1/L2 失败红色（阻断）、L3/告警黄色、skipped 灰色 */
function StratifiedQaLayers({ stratified }: { stratified: StratifiedQaReport }) {
  const styleOf = (layer: StratifiedQaReport['layers'][number]) => {
    if (layer.status === 'skipped') return { dot: 'bg-muted-foreground/40', text: 'text-muted-foreground', badge: '跳过' };
    if (layer.status === 'failed' && layer.blocking) return { dot: 'bg-red-400', text: 'text-red-400', badge: '阻断' };
    if (layer.status === 'failed' || layer.status === 'warning') return { dot: 'bg-amber-400', text: 'text-amber-400', badge: '告警' };
    return { dot: 'bg-neon-green', text: 'text-neon-green', badge: '通过' };
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-medium text-foreground">分层 QA 门禁</span>
        <span className={stratified.passed ? 'text-neon-green' : 'text-red-400'}>
          {stratified.passed ? '✅ 通过' : `❌ 未通过（${stratified.failedAt} 阻断）`}
        </span>
      </div>
      {stratified.layers.map(layer => {
        const s = styleOf(layer);
        return (
          <div key={layer.level} className="rounded bg-muted/20 p-2">
            <div className="flex items-center gap-2 text-xs">
              <span className={cn('h-2 w-2 shrink-0 rounded-full', s.dot)} />
              <span className="font-mono text-[10px] text-muted-foreground">{layer.level}</span>
              <span className={cn('flex-1 truncate', s.text)}>{layer.name}</span>
              <span className={cn('shrink-0 rounded px-1.5 py-0.5 text-[10px]', s.text)}>{s.badge}</span>
            </div>
            {layer.issues.length > 0 && (
              <div className="mt-1 space-y-0.5 pl-4">
                {layer.issues.slice(0, 5).map((issue, i) => (
                  <p key={i} className={cn('text-[10px]', s.text, 'opacity-80')}>• {issue}</p>
                ))}
                {layer.issues.length > 5 && <p className="text-[10px] text-muted-foreground">…共 {layer.issues.length} 项</p>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function JsonTree({ data, depth = 0 }: { data: unknown; depth?: number }) {
  if (data === null || data === undefined) return <span className="text-muted-foreground/50">null</span>;
  if (typeof data === 'string') return <span className="text-neon-green">"{data.length > 100 ? data.slice(0, 100) + '...' : data}"</span>;
  if (typeof data === 'number') return <span className="text-neon-cyan">{data}</span>;
  if (typeof data === 'boolean') return <span className="text-neon-purple">{String(data)}</span>;
  if (depth > 4) return <span className="text-muted-foreground">{JSON.stringify(data).slice(0, 50)}</span>;

  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="text-muted-foreground">[]</span>;
    return <JsonNode label={`Array[${data.length}]`} defaultOpen={true}>
      {data.map((item, i) => <div key={i} className="ml-2">{i}: <JsonTree data={item} depth={depth + 1} /></div>)}
    </JsonNode>;
  }

  if (typeof data === 'object') {
    const entries = Object.entries(data as Record<string, unknown>);
    if (entries.length === 0) return <span className="text-muted-foreground">{'{}'}</span>;
    return <JsonNode label={`Object{${entries.length}}`} defaultOpen={true}>
      {entries.map(([k, v]) => <div key={k} className="ml-2">{k}: <JsonTree data={v} depth={depth + 1} /></div>)}
    </JsonNode>;
  }

  return <span>{String(data)}</span>;
}

function JsonNode({ label, defaultOpen, children }: { label: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen ?? true);
  return (
    <div className="ml-2">
      <button onClick={() => setOpen(!open)} className="text-muted-foreground hover:text-foreground">
        {open ? '▾ ' : '▸ '}{label}
      </button>
      {open && <div className="border-l border-border/20 ml-1 pl-2">{children}</div>}
    </div>
  );
}

// P1-2 输入流水线「证据 vs 推断」标注（局部类型定义，与 agent-server types/input-annotations.ts 对齐；
// 按约束不进 agent-sdk，数据源为 session metadata.videoInputAnalysis）
interface VideoInputAnalysis {
  evidence: Array<{ aspect: string; description: string; timestamp?: string; source: string }>;
  inferred: Array<{ inferred: true; aspect: string; description: string; timestamp?: string; basis?: string; confidence?: string }>;
  truthMap: Array<{ timestamp: string; behavior: string; evidenceType: 'observed' | 'inferred' }>;
  updatedAt?: string;
}

const CONFIDENCE_LABEL: Record<string, string> = { high: '高', medium: '中', low: '低' };

/** 视频输入分析分区展示：证据（绿）与推断（琥珀，带「推断」标签）+ TruthMap 时间戳映射 */
function VideoAnalysisSection({ analysis }: { analysis: VideoInputAnalysis }) {
  return (
    <CollapsibleSection title="视频输入分析（证据 vs 推断）">
      <div className="space-y-3">
        {analysis.evidence.length > 0 && (
          <div>
            <span className="text-[10px] font-medium text-neon-green">✅ 可见证据 ({analysis.evidence.length})</span>
            <div className="mt-1 space-y-0.5">
              {analysis.evidence.map((e, i) => (
                <div key={i} className="flex items-start gap-2 rounded bg-muted/20 px-2 py-1 text-xs">
                  <span className="shrink-0 rounded bg-neon-green/10 px-1.5 py-0.5 text-[10px] text-neon-green">证据</span>
                  <span className="text-foreground/80">
                    <span className="text-muted-foreground">[{e.aspect}]</span> {e.description}
                    {e.timestamp && <span className="ml-1 text-muted-foreground/60">@{e.timestamp}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {analysis.inferred.length > 0 && (
          <div>
            <span className="text-[10px] font-medium text-amber-400">⚠️ AI 推断（非事实，请确认）({analysis.inferred.length})</span>
            <div className="mt-1 space-y-0.5">
              {analysis.inferred.map((it, i) => (
                <div key={i} className="flex items-start gap-2 rounded bg-amber-500/5 px-2 py-1 text-xs">
                  <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-400">推断</span>
                  <span className="text-foreground/80">
                    <span className="text-muted-foreground">[{it.aspect}]</span> {it.description}
                    {it.basis && <span className="ml-1 text-muted-foreground/60">依据: {it.basis}</span>}
                    {it.confidence && <span className="ml-1 text-amber-400/70">置信度: {CONFIDENCE_LABEL[it.confidence] ?? it.confidence}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {analysis.truthMap.length > 0 && (
          <CollapsibleSection title={`TruthMap 时间戳映射 (${analysis.truthMap.length})`} defaultOpen={false}>
            <div className="space-y-0.5">
              {analysis.truthMap.map((t, i) => (
                <div key={i} className="flex items-center gap-2 rounded bg-muted/20 px-2 py-1 text-xs">
                  <span className="w-16 shrink-0 font-mono text-neon-cyan">{t.timestamp}</span>
                  <span className="flex-1 text-foreground/80">{t.behavior}</span>
                  <span className={t.evidenceType === 'observed' ? 'shrink-0 rounded bg-neon-green/10 px-1.5 py-0.5 text-[10px] text-neon-green' : 'shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] text-amber-400'}>
                    {t.evidenceType === 'observed' ? '可见' : '推断'}
                  </span>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        )}
      </div>
    </CollapsibleSection>
  );
}

function CollapsibleSection({ title, defaultOpen, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(defaultOpen ?? true);
  return (
    <div className="space-y-1.5">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-xs font-medium text-muted-foreground hover:bg-muted/30 hover:text-foreground">
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}{title}
      </button>
      {open && <div className="pl-3">{children}</div>}
    </div>
  );
}

export function WorkflowPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { nodes: rawOutputs, allDone, refresh } = usePipelinePoll(sessionId ?? null);

  // WebSocket 实时推送：收到 asset:ready 事件立即刷新节点数据（素材产出不再只靠 2s 轮询）
  const { subscribe } = useWebSocket(sessionId ?? null);
  useEffect(() => {
    return subscribe('asset:ready', () => {
      refresh();
    });
  }, [subscribe, refresh]);
  // 视频转试玩模式：orchestrator 广播 pipeline:stage 节点事件 → 立即刷新节点图
  useEffect(() => {
    return subscribe('pipeline:stage', () => {
      refresh();
    });
  }, [subscribe, refresh]);
  // SSE 流水线事件驱动刷新：轮询仅在「有 running 节点」时启动，GDD 确认/段暂停等时刻
  // 无轮询覆盖，必须靠事件 bump 主动拉一次，否则节点状态要等手动刷新页面才显示。
  const pipelineTick = useAgentStore((s) => s.pipelineTick);
  const lastTickRef = useRef(0);
  useEffect(() => {
    if (pipelineTick !== lastTickRef.current) {
      lastTickRef.current = pipelineTick;
      refresh();
    }
  }, [pipelineTick]);
  const isVideoMode = Boolean(rawOutputs['video-start']);
  const nodeDefs = isVideoMode ? VIDEO_NODE_DEFS : BASE_NODE_DEFS;

  // 标准轨兼容旧 key；视频轨严格按真实 video-* 节点聚合，避免错误映射到模板节点。
  const outputs = useMemo(() => {
    if (isVideoMode) {
      const grouped: Record<string, NodeOutput> = {};
      const terminalDone = rawOutputs.done?.status === 'done' || rawOutputs.publish?.status === 'done';
      for (const def of VIDEO_NODE_DEFS) {
        const present = def.nodeKeys.map((key) => rawOutputs[key]).filter(Boolean) as NodeOutput[];
        const selected = (terminalDone ? [...present].reverse().find((node) => node.status === 'done') : undefined)
          ?? present.find((node) => node.status === 'running')
          ?? present.find((node) => node.status === 'failed')
          ?? [...present].reverse().find((node) => node.status === 'done')
          ?? present[0];
        if (selected) grouped[def.key] = terminalDone ? { ...selected, status: 'done', progress: 100 } : selected;
        else if (terminalDone) grouped[def.key] = { status: 'done', progress: 100, duration: 0 } as NodeOutput;
      }
      return grouped;
    }
    const merged: Record<string, NodeOutput> = {};
    const entries = Object.entries(rawOutputs);
    for (const [k, v] of entries) { if (!KEY_ALIAS[k]) merged[k] = v; }
    for (const [k, v] of entries) {
      const nk = KEY_ALIAS[k];
      if (nk && !merged[nk]) merged[nk] = v;
    }
    return merged;
  }, [rawOutputs, isVideoMode]);
  const initialSelectedKey = (location.state as { selectedNode?: string })?.selectedNode ?? null;
  const [selectedKey, setSelectedKey] = useState<string | null>(initialSelectedKey ? (KEY_ALIAS[initialSelectedKey] ?? initialSelectedKey) : null);
  const [imgTab, setImgTab] = useState<string>('全部');
  /** 素材大图预览（点击网格小图弹出，含 prompt 链路） */
  const [previewImg, setPreviewImg] = useState<NonNullable<NodeOutput['images']>[number] | null>(null);
  const [logExpanded, setLogExpanded] = useState(true);
  const [splitRatio, setSplitRatio] = useState(40); // % for top panel
  const splitDrag = useRef(false);
  // P1-2：视频输入分析标注（证据/推断/TruthMap），来自 session metadata.videoInputAnalysis
  const [videoAnalysis, setVideoAnalysis] = useState<VideoInputAnalysis | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`/api/agent/session/${sessionId}`);
        if (!res.ok) return;
        const json = await res.json();
        const va = json?.data?.metadata?.videoInputAnalysis as VideoInputAnalysis | undefined;
        if (!cancelled && va && (va.evidence?.length || va.inferred?.length)) {
          setVideoAnalysis({ evidence: va.evidence ?? [], inferred: va.inferred ?? [], truthMap: va.truthMap ?? [], updatedAt: va.updatedAt });
        }
      } catch { /* 非阻断：无标注时静默 */ }
    })();
    return () => { cancelled = true; };
  }, [sessionId, allDone]);

  const selected = selectedKey ? outputs[selectedKey] : null;

  const initialNodes: Node[] = useMemo(() => nodeDefs.map((def, i) => ({
    id: def.key, type: 'flowNode', position: { x: 50, y: i * 80 + 20 },
    data: { label: def.label, status: outputs[def.key]?.status ?? 'pending', duration: outputs[def.key]?.duration, startedAt: outputs[def.key]?.startedAt, selected: selectedKey === def.key },
  })), [nodeDefs, outputs, selectedKey]);

  const initialEdges: Edge[] = useMemo(() => nodeDefs.slice(0, -1).map((def, i) => ({
    id: `${def.key}-${nodeDefs[i + 1].key}`, source: def.key, target: nodeDefs[i + 1].key,
    style: { stroke: outputs[def.key]?.status === 'done' ? '#22c55e' : '#334155', strokeWidth: 1.5 },
    animated: outputs[def.key]?.status === 'running',
  })), [nodeDefs, outputs]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  useMemo(() => {
    setNodes(nodeDefs.map((def, i) => ({ id: def.key, type: 'flowNode', position: { x: 50, y: i * 80 + 20 }, data: { label: def.label, status: outputs[def.key]?.status ?? 'pending', duration: outputs[def.key]?.duration, startedAt: outputs[def.key]?.startedAt, selected: selectedKey === def.key } })));
    setEdges(nodeDefs.slice(0, -1).map((def, i) => ({ id: `${def.key}-${nodeDefs[i + 1].key}`, source: def.key, target: nodeDefs[i + 1].key, style: { stroke: outputs[def.key]?.status === 'done' ? '#22c55e' : '#334155', strokeWidth: 1.5 }, animated: outputs[def.key]?.status === 'running' })));
  }, [nodeDefs, outputs, selectedKey, setNodes, setEdges]);

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => setSelectedKey(prev => prev === node.id ? null : node.id), []);

  return (
    <div className="flex h-dvh flex-col bg-background">
      <div className="flex shrink-0 items-center gap-3 border-b border-border/30 px-4 py-3">
        <button onClick={() => navigate(-1)} className="icon-btn rounded p-1.5"><ArrowLeft size={20} /></button>
        <span className="text-sm font-medium">工作流</span>
        <span className="text-[11px] text-muted-foreground">{sessionId?.slice(0, 8)}</span>
        {allDone && <span className="ml-auto text-xs text-neon-green">全部完成</span>}
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left: Flow */}
        <div className="w-[240px] shrink-0 border-r border-border/30">
          <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onNodeClick={onNodeClick} nodeTypes={nodeTypes} fitView proOptions={{ hideAttribution: true }} />
        </div>

        {/* Right: Detail (left: outputs, right: logs) */}
        <div className="flex flex-1 flex-row overflow-hidden">
          {selected ? (
            <>
              {/* Left: Outputs */}
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {/* 节点失败信息：任何节点失败统一在此展示（统一 prototype_build 入口） */}
                {selected.error && (
                  <div className="mb-3 rounded border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-400">
                    <span className="font-medium">❌ 执行失败：</span>{selected.error}
                  </div>
                )}
                {/* Requirement: show GDD / config JSON */}
                {selectedKey === 'requirement' && selected.artifacts && (
                  <CollapsibleSection title="需求分析产出">
                    <div className="max-h-96 overflow-y-auto rounded-lg bg-muted/30 p-3 text-[11px] leading-relaxed font-mono"><JsonTree data={selected.artifacts} /></div>
                  </CollapsibleSection>
                )}

                {/* Planning: GDD 文本卡 + 素材清单 + plan steps */}
                {selectedKey === 'planning' && selected.artifacts && (
                  <CollapsibleSection title="方案规划产出">
                    {(() => {
                      const art = selected.artifacts as Record<string,unknown>;
                      const nodes = art.nodes as Array<{tool:string;label:string}> | undefined;
                      const risks = art.risks as string[] | undefined;
                      const routeMode = art.routeMode as string | undefined;
                      const gameplayType = art.gameplayType as string | undefined;
                      const assetPlan = art.assetPlan as Array<{ slotName: string; category: string; transparent: boolean; size?: string; prompt: string }> | undefined;
                      return (
                        <div className="space-y-2">
                          {/* 命中模式：模板 / 蓝图（artifacts.routeMode 优先，fallback selected.mode，无值不显示标签） */}
                          <div className="flex items-center gap-2 rounded bg-muted/20 px-3 py-2">
                            <span className="text-[10px] text-muted-foreground">生成路径</span>
                            {(() => {
                              const mode = routeMode || (selected.mode as string | undefined);
                              if (mode) {
                                return (
                                  <span className={cn('rounded px-2 py-0.5 text-[11px] font-medium',
                                    mode === 'template' ? 'bg-neon-green/15 text-neon-green' : 'bg-neon-purple/15 text-neon-purple')}>
                                    {mode === 'template' ? '模板装配' : '蓝图拼装'}
                                  </span>
                                );
                              }
                              return <span className="text-[11px] text-muted-foreground/50">—</span>;
                            })()}
                            {gameplayType && <span className="text-[11px] text-muted-foreground">{gameplayType}</span>}
                          </div>
                          {(() => {
                            // 完整 GDD 原文（新数据）：复用会话弹窗同一渲染组件，纵向单列完整展示
                            const gdd = art.gdd as GddSpec | undefined;
                            if (gdd) {
                              return (
                                <div className="space-y-2">
                                  <span className="text-[10px] font-medium text-muted-foreground">GDD 方案</span>
                                  <GddDialogContent spec={gdd} vertical />
                                </div>
                              );
                            }
                            // 老会话数据兜底：将 gddToTextLines 产出的 "键：值" 行解析回结构化
                            const gddText = art.gddText as string[] | undefined;
                            if (!gddText || gddText.length === 0) return null;
                            const map: Record<string, string> = {};
                            for (const line of gddText) {
                              const m = line.match(/^(.+?)[：:]\s*(.+)/);
                              if (m) map[m[1]] = m[2];
                            }
                            const hasAny = Object.keys(map).length > 0;
                            if (!hasAny) return null;
                            // 精简分组：只展示有值的分组
                            const groups = [
                              { title: '核心玩法', keys: ['玩法', '主题', '难度', '核心循环', '胜利条件', '失败条件', '试玩时长'] },
                              { title: '视觉', keys: ['美术风格', '配色', 'UI风格', '镜头效果'] },
                              { title: '动效与音频', keys: ['动效', '粒子特效', '音频风格'] },
                              { title: '关卡', keys: ['关卡设计', '特色元素'] },
                            ];
                            return (
                              <div className="space-y-2">
                                <span className="text-[10px] font-medium text-muted-foreground">GDD 方案</span>
                                <div className="rounded-lg border border-neon-cyan/10 bg-muted/20 p-3 space-y-2.5">
                                  {groups.map((g) => {
                                    const rows = g.keys.filter((k) => map[k]);
                                    if (!rows.length) return null;
                                    return (
                                      <div key={g.title}>
                                        <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">{g.title}</span>
                                        <div className="mt-1 grid grid-cols-1 gap-y-0.5">
                                          {rows.map((k) => (
                                            <div key={k} className="flex items-baseline gap-1 text-xs">
                                              <span className="shrink-0 text-muted-foreground/70">{k}</span>
                                              <span className="min-w-0 flex-1 break-words text-foreground/85">{map[k]}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {/* 玩法参数（parameters 展平为 kv） */}
                                  {(() => {
                                    const paramKeys = Object.keys(map).filter((k) => !groups.some((g) => g.keys.includes(k)));
                                    if (!paramKeys.length) return null;
                                    return (
                                      <div>
                                        <span className="text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/60">参数</span>
                                        <div className="mt-1 grid grid-cols-1 gap-y-0.5">
                                          {paramKeys.map((k) => (
                                            <div key={k} className="flex items-baseline gap-1 text-xs">
                                              <span className="shrink-0 text-muted-foreground/70">{k}</span>
                                              <span className="min-w-0 flex-1 break-words font-mono text-[11px] text-foreground/85">{map[k]}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </div>
                            );
                          })()}
                          {assetPlan && assetPlan.length > 0 && (
                            <div>
                              <span className="text-[10px] font-medium text-muted-foreground">素材清单（{assetPlan.length} 个槽位，点击展开 prompt）</span>
                              <div className="mt-1 space-y-1">
                                {assetPlan.map((p) => (
                                  <details key={p.slotName} className="rounded bg-muted/20 px-2 py-1 text-xs">
                                    <summary className="flex cursor-pointer items-center gap-1.5">
                                      <span className="font-mono text-foreground/90">{p.slotName}</span>
                                      <span className="rounded bg-primary/10 px-1 py-px text-[9px] text-primary">{p.category}</span>
                                      {p.transparent && <span className="rounded bg-neon-purple/10 px-1 py-px text-[9px] text-neon-purple">透明底·抠图</span>}
                                      {p.size && <span className="ml-auto text-[9px] text-muted-foreground">{p.size}</span>}
                                    </summary>
                                    <p className="mt-1 whitespace-pre-wrap text-[11px] leading-relaxed text-muted-foreground">{p.prompt}</p>
                                  </details>
                                ))}
                              </div>
                            </div>
                          )}
                          {nodes && (
                            <div>
                              <span className="text-[10px] font-medium text-muted-foreground">任务流水线 ({nodes.length} 步)</span>
                              <div className="mt-1 space-y-0.5">
                                {nodes.map((n, i) => (
                                  <div key={i} className="flex items-center gap-2 rounded bg-muted/20 px-2 py-1 text-xs">
                                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-[10px] font-medium text-primary">{i + 1}</span>
                                    <span className="text-muted-foreground">{n.tool}</span>
                                    <span className="ml-auto text-foreground/70">{n.label}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {art.estimatedDuration && <div className="flex items-center gap-2 text-xs"><span className="text-muted-foreground">预计耗时</span><span className="text-foreground">{Math.round((art.estimatedDuration as number) / 1000)}s</span></div>}
                          {art.executionMode && <div className="flex items-center gap-2 text-xs"><span className="text-muted-foreground">执行模式</span><span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary">{(art.executionMode as string) === 'full' ? '全量执行' : '增量执行'}</span></div>}
                          {risks && risks.length > 0 && (
                            <div><span className="text-[10px] font-medium text-amber-400">风险提示</span>
                              {risks.map((r, i) => <div key={i} className="mt-0.5 text-xs text-amber-400/80">⚠️ {r}</div>)}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </CollapsibleSection>
                )}

                {/* P1-2: 视频转试玩链路的证据/推断分区展示（挂在方案规划节点详情下） */}
                {selectedKey === 'planning' && videoAnalysis && (
                  <div className="mt-3">
                    <VideoAnalysisSection analysis={videoAnalysis} />
                  </div>
                )}

                {/* 素材产出：tab 分类 + 小图网格（点击弹大图预览 + prompt 链路） */}
                {selectedKey === 'asset_prepare' && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-medium text-foreground">素材产出</h4>
                    <div className="flex gap-1 overflow-x-auto pb-1">
                      {IMAGE_CATEGORIES.map(cat => (
                        <button key={cat} onClick={() => setImgTab(cat)} className={cn('shrink-0 rounded-full px-2.5 py-0.5 text-[11px]', imgTab === cat ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground')}>{cat}</button>
                      ))}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {!selected.images || selected.images.length === 0 ? '暂无素材产出' : (
                        <div className="grid grid-cols-5 gap-1.5">
                          {selected.images.filter(img => imgTab === '全部' || slotCategoryLabel(img.slot) === imgTab).map((img, i) => (
                            <button key={i} className="space-y-0.5 text-left" onClick={() => img.url && setPreviewImg(img)} title="点击预览大图与 prompt">
                              <div className="aspect-square overflow-hidden rounded-md bg-muted">
                                {img.url ? <img src={img.url} alt={img.slot} className="h-full w-full cursor-zoom-in object-cover transition-transform hover:scale-105" /> : <div className="flex h-full items-center justify-center text-muted-foreground">{img.slot}</div>}
                              </div>
                              <p className="truncate text-center text-[9px]">{img.slot}</p>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Build: package output */}
                {selectedKey === 'publish' && (
                  <CollapsibleSection title="构建产出">
                    {selected.previewUrl && (
                      <div className="rounded bg-muted/20 p-2 text-xs"><span className="text-muted-foreground">预览地址: </span><a href={selected.previewUrl} target="_blank" className="text-neon-cyan underline break-all" rel="noreferrer">{selected.previewUrl}</a></div>
                    )}
                    {selected.downloadUrl && (
                      <div className="rounded bg-muted/20 p-2 text-xs"><span className="text-muted-foreground">下载地址: </span><a href={selected.downloadUrl} target="_blank" className="text-neon-cyan underline break-all" rel="noreferrer">{selected.downloadUrl}</a></div>
                    )}
                    {selected.artifacts && (
                      <CollapsibleSection title="完整数据 JSON">
                        <div className="max-h-96 overflow-y-auto rounded-lg bg-muted/30 p-3 text-[11px] leading-relaxed font-mono"><JsonTree data={selected.artifacts} /></div>
                      </CollapsibleSection>
                    )}
                  </CollapsibleSection>
                )}

                {/* QA: scores + verdict + AI summary */}
                {selectedKey === 'qa_check' && (
                  <CollapsibleSection title="质量检测报告">
                    {/* P1-1: 分层 QA 门禁报告（match3 灰度路径才有 stratified） */}
                    {!!(selected.artifacts?.stratified as StratifiedQaReport | undefined)?.layers?.length && (
                      <StratifiedQaLayers stratified={selected.artifacts!.stratified as StratifiedQaReport} />
                    )}
                    {selected.verdict && (
                      <span className={`text-xs font-medium ${selected.verdict === 'pass' ? 'text-neon-green' : selected.verdict === 'fail' ? 'text-red-400' : 'text-amber-400'}`}>
                        {selected.verdict === 'pass' ? '✅ 通过' : selected.verdict === 'fail' ? '❌ 未通过' : '⚠️ 有条件通过'}
                      </span>
                    )}
                    {selected.scores && Object.entries(selected.scores).map(([k, v]) => (
                      <div key={k} className="flex items-center gap-2 text-xs">
                        <span className="w-28 text-muted-foreground">{k}</span>
                        <div className="h-1.5 flex-1 rounded-full bg-muted"><div className="progress-neon h-full rounded-full" style={{ width: `${v}%` }} /></div>
                        <span className="w-8 text-right">{v}</span>
                      </div>
                    ))}
                    {selected.artifacts && <div className="max-h-96 overflow-y-auto rounded-lg bg-muted/30 p-3 text-[11px] leading-relaxed font-mono"><JsonTree data={selected.artifacts} /></div>}
                  </CollapsibleSection>
                )}

                {/* Prototype Build：命中模式/模板/蓝图 + 素材注入 */}
                {selectedKey === 'prototype_build' && (
                  <CollapsibleSection title="原型构建产出">
                    <div className="flex flex-wrap gap-2 text-xs">
                      {selected.mode && <span className="rounded bg-primary/10 px-2 py-0.5">模式: {selected.mode}</span>}
                      {selected.gameplay && <span className="rounded bg-neon-purple/10 px-2 py-0.5">玩法: {selected.gameplay}</span>}
                      {selected.skillName && <span className="rounded bg-neon-cyan/10 px-2 py-0.5">模板: {selected.skillName}</span>}
                    </div>
                    {(() => {
                      const art = selected.artifacts as Record<string, unknown> | undefined;
                      if (!art) return null;
                      const bs = art.blueprintSummary as { gameplayType?: string; theme?: string; visualStyle?: string; difficulty?: string } | undefined;
                      const quality = art.quality as { score?: number } | undefined;
                      const injected = art.injectedSlots as string[] | undefined;
                      return (
                        <div className="mt-2 space-y-1 text-xs">
                          <div className="rounded bg-muted/20 p-2">
                            <span className="text-muted-foreground">命中模式：</span>
                            <span className="text-foreground">{art.assembleMode === 'template' ? '模板装配（玩法模板 + AI 素材点缀）' : String(art.assembleMode ?? selected.mode ?? '-')}</span>
                          </div>
                          {bs && (
                            <div className="rounded bg-muted/20 p-2">
                              <span className="text-muted-foreground">玩法蓝图：</span>
                              <span className="text-foreground">{bs.gameplayType} · {bs.theme} · {bs.visualStyle} · {bs.difficulty}</span>
                            </div>
                          )}
                          {injected && injected.length > 0 && (
                            <div className="rounded bg-muted/20 p-2">
                              <span className="text-muted-foreground">素材注入：</span>
                              <span className="text-foreground">{injected.length} 个槽位（{injected.slice(0, 8).join('、')}{injected.length > 8 ? '…' : ''}）</span>
                            </div>
                          )}
                          <div className="flex gap-2">
                            {quality?.score != null && (
                              <div className="flex-1 rounded bg-muted/20 p-2">
                                <span className="text-muted-foreground">装配质量：</span>
                                <span className="text-foreground">{quality.score} 分</span>
                              </div>
                            )}
                            {selected.fileSize != null && (
                              <div className="flex-1 rounded bg-muted/20 p-2">
                                <span className="text-muted-foreground">包体：</span>
                                <span className="text-foreground">{(selected.fileSize / 1024).toFixed(0)} KB</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    {selected.artifacts && <div className="mt-2 max-h-96 overflow-y-auto rounded-lg bg-muted/30 p-3 text-[11px] leading-relaxed font-mono"><JsonTree data={selected.artifacts} /></div>}
                  </CollapsibleSection>
                )}
              </div>

              {/* Right: Logs（方案规划节点不显示日志） */}
              {selectedKey !== 'planning' && (
                <div className="flex w-[480px] shrink-0 flex-col border-l border-border/30">
                  <button onClick={() => setLogExpanded(!logExpanded)} className="flex items-center gap-1 px-4 py-2 text-xs font-medium text-foreground hover:bg-muted/30">
                    {logExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    {nodeDefs.find(d => d.key === selectedKey)?.label} — 执行日志
                    <span className="ml-auto text-[10px] text-muted-foreground">{selected.status} · {(selected.duration ?? 0) / 1000}s</span>
                  </button>
                  {logExpanded && (
                    <div className="flex-1 overflow-y-auto px-4 pb-3">
                      {selected.logs && selected.logs.length > 0 ? (
                        <div className="space-y-0.5 font-mono text-xs leading-relaxed">
                          {selected.logs.map((l, i) => (
                            <div key={i} className={l.level === 'error' ? 'text-red-400' : l.level === 'warn' ? 'text-amber-400' : 'text-muted-foreground'}>
                              <span className="select-none text-muted-foreground/40">{new Date(l.ts).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span> {l.text}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="py-4 text-center text-xs text-muted-foreground">暂无日志</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center text-xs text-muted-foreground">点击节点查看详情</div>
          )}

          {/* 素材大图预览弹层：小图 → 大图 + 槽位信息 + prompt 链路（规划→增强） */}
          {previewImg && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setPreviewImg(null)}>
              <div className="max-h-full w-[360px] overflow-y-auto rounded-xl border border-border/50 bg-card p-3" onClick={(e) => e.stopPropagation()}>
                <img src={previewImg.url} alt={previewImg.slot} className="w-full rounded-lg bg-muted object-contain" />
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <span className="font-mono text-xs text-foreground">{previewImg.slot}</span>
                  <span className="rounded bg-primary/10 px-1.5 py-px text-[10px] text-primary">{slotCategoryLabel(previewImg.slot)}</span>
                  {previewImg.transparent && <span className="rounded bg-neon-purple/10 px-1.5 py-px text-[10px] text-neon-purple">透明底·已抠图</span>}
                </div>
                {previewImg.prompt && (
                  <div className="mt-2">
                    <p className="text-[10px] font-medium text-muted-foreground">规划 prompt（槽位模板）</p>
                    <p className="mt-0.5 whitespace-pre-wrap text-[11px] leading-relaxed text-muted-foreground/90">{previewImg.prompt}</p>
                  </div>
                )}
                {previewImg.enhancedPrompt && previewImg.enhancedPrompt !== previewImg.prompt && (
                  <div className="mt-2">
                    <p className="text-[10px] font-medium text-muted-foreground">增强后 prompt（执行期：槽位覆盖词 → 分类增强 → 风格/负向约束）</p>
                    <p className="mt-0.5 whitespace-pre-wrap text-[11px] leading-relaxed text-muted-foreground/90">{previewImg.enhancedPrompt}</p>
                  </div>
                )}
                <button className="mt-3 w-full rounded-md border border-border/50 py-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => setPreviewImg(null)}>关闭</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
